import { LobbyImage } from '@colonist/types';
import { MapSettingEnum, ModeSettingEnum } from '@colonist/game-base-types';
import { ColonistMembershipPerkType } from '@colonist/lobby-types';
import { MapDataController } from '../../../../../shared/map_data/MapDataController.ts';
import { sendBotsTabMapSelectedAnalytic } from '../../analytics/sendBotsTabAnalytic.ts';
import { ClientUserState } from '../../ClientUserState.ts';
import { StorageWrapper } from '../../StorageWrapper.ts';
import { UIUtilsEjsVariables } from '../../UIUtilsEjsVariables.ts';
import { UIComponentItemScroll } from '../../ui_components/item_scroll/UIComponentItemScroll.ts';
import { showMapInfoDialog } from '../../ui_popup/showMapInfoDialog.ts';
import { createDivIn, createImgIn } from '../../ui_utils/DomUtils.ts';
import { isMapLockedForUser } from '../../ui_utils/isMapLockedForUser.ts';
export class UIModeSelectorBotMapController {
    getSelectedMapSetting() {
        return this.selectedMapSetting;
    }
    static shouldShowMembershipButton(mapSetting) {
        if (mapSetting == undefined) return false;
        return !UIModeSelectorBotMapController.hasMapSettingAccess(mapSetting);
    }
    static hasMapSettingAccess(mapSetting) {
        const mapData = MapDataController.getMapDataForMap(mapSetting);
        if (mapData == undefined) return false;
        return UIModeSelectorBotMapController.isMapAvailable(mapData);
    }
    update() {
        this.setSelectedMapToDefaultIfUnselectable();
        this.mapSelector.update(this.getMapItemData());
        this.updateMapSelectorInteractability();
    }
    static stopCardChildPropagation(element) {
        element.addEventListener('touchstart', (event)=>event.stopPropagation());
        element.addEventListener('touchend', (event)=>event.stopPropagation());
        // eslint-disable-next-line no-restricted-syntax
        element.addEventListener('click', (event)=>event.stopPropagation());
    }
    static createMapSelectorContainer(parentCardElement) {
        const mapSelectorContainer = createDivIn(parentCardElement, 'item-scroll mm-bot-map-selector m-no-drag');
        UIModeSelectorBotMapController.stopCardChildPropagation(mapSelectorContainer);
        createImgIn(mapSelectorContainer, LobbyImage.LeftArrow, 'scroll-arrow left-arrow');
        const scrollWrapper = createDivIn(mapSelectorContainer, 'item-scroll-wrapper');
        createDivIn(scrollWrapper, 'item-scroll-container');
        createImgIn(mapSelectorContainer, LobbyImage.LeftArrow, 'scroll-arrow right-arrow');
        return mapSelectorContainer;
    }
    updateMapSelectorInteractability() {
        this.mapSelector.updateInteractabilityOfElements(false);
    }
    setSelectedMapToDefaultIfUnselectable() {
        const selectedMapData = MapDataController.getMapDataForMap(this.selectedMapSetting);
        if (selectedMapData == undefined || !UIModeSelectorBotMapController.isMapSelectable(selectedMapData)) {
            this.selectedMapSetting = UIModeSelectorBotMapController.DEFAULT_MAP_SETTING;
        }
    }
    static getInitialMapSetting() {
        const lastSelectedMap = StorageWrapper.getLastSelectedBotMap();
        if (lastSelectedMap != null) {
            const lastSelectedMapData = MapDataController.getMapDataForMap(lastSelectedMap);
            const isMapRestorable = lastSelectedMapData != undefined && UIModeSelectorBotMapController.isMapSelectable(lastSelectedMapData) && UIModeSelectorBotMapController.isMapAvailable(lastSelectedMapData);
            if (isMapRestorable) return lastSelectedMap;
        }
        const freeMapData = MapDataController.getMapDataForMap(UIUtilsEjsVariables.freeMap);
        if (freeMapData != undefined && UIModeSelectorBotMapController.isMapSelectable(freeMapData)) return UIUtilsEjsVariables.freeMap;
        return UIModeSelectorBotMapController.DEFAULT_MAP_SETTING;
    }
    static isMapSelectable(mapData) {
        if (!mapData.isActive) return false;
        if (!mapData.compatibleGameModes.includes(UIModeSelectorBotMapController.BOT_MAP_MODE_SETTING)) return false;
        const isMapUnavailable = !UIModeSelectorBotMapController.isMapAvailable(mapData);
        const isMapHidden = isMapUnavailable && UIUtilsEjsVariables.shouldHideStoreOnMobile;
        if (isMapHidden) return false;
        return true;
    }
    getMapItemData() {
        const compatibleMapData = MapDataController.getMapDataCompatibleWithGameMode(UIModeSelectorBotMapController.BOT_MAP_MODE_SETTING);
        const mapItemData = [];
        for (const mapData of compatibleMapData){
            if (!mapData.isActive) continue;
            const isMapAvailable = UIModeSelectorBotMapController.isMapAvailable(mapData);
            if (!isMapAvailable && UIUtilsEjsVariables.shouldHideStoreOnMobile) continue;
            const itemData = {
                name: UIModeSelectorBotMapController.getMapDisplayText(mapData),
                image: mapData.image,
                available: isMapAvailable,
                selected: mapData.mapEnum == this.selectedMapSetting,
                bannerText: UIModeSelectorBotMapController.shouldShowWeeklyFreeMapBanner(mapData) ? {
                    key: 'strings:roomPage.options.map.weeklyFree'
                } : undefined,
                clickAction: ()=>this.handleMapClick(mapData),
                infoAction: ()=>this.handleMapInfoClick(mapData, isMapAvailable),
                showInfoIcon: true
            };
            mapItemData.push(itemData);
        }
        return mapItemData;
    }
    handleMapClick(mapData) {
        this.selectedMapSetting = mapData.mapEnum;
        StorageWrapper.setLastSelectedBotMap(mapData.mapEnum);
        sendBotsTabMapSelectedAnalytic(mapData.mapEnum, !UIModeSelectorBotMapController.isMapAvailable(mapData));
        if (this.shouldSelectBotDifficultyCard()) this.selectBotDifficultyCard();
        this.update();
        this.onSelectedMapChange();
    }
    handleMapInfoClick(mapData, isMapAvailable) {
        if (isMapAvailable && this.shouldSelectBotDifficultyCard()) {
            this.selectBotDifficultyCard();
            return;
        }
        showMapInfoDialog(mapData);
    }
    static isMapAvailable(mapData) {
        return !isMapLockedForUser(mapData);
    }
    static shouldShowWeeklyFreeMapBanner(mapData) {
        var _ClientUserState_userState;
        if ((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.items.isPerkAvailable(ColonistMembershipPerkType.AllMaps)) return false;
        if (mapData.mapEnum == UIUtilsEjsVariables.freeMap) return true;
        return false;
    }
    static getMapDisplayText(mapData) {
        switch(mapData.mapEnum){
            case MapSettingEnum.Classic56P:
                return {
                    key: 'strings:colonistCommon.gameMaps.classic.56P.abbreviation'
                };
            case MapSettingEnum.Classic78P:
                return {
                    key: 'strings:colonistCommon.gameMaps.classic.78P.abbreviation'
                };
            default:
                return mapData.name;
        }
    }
    constructor(params){
        this.selectedMapSetting = UIModeSelectorBotMapController.getInitialMapSetting();
        this.shouldSelectBotDifficultyCard = params.shouldSelectBotDifficultyCard;
        this.selectBotDifficultyCard = params.selectBotDifficultyCard;
        this.onSelectedMapChange = params.onSelectedMapChange;
        const mapSelectorContainer = UIModeSelectorBotMapController.createMapSelectorContainer(params.parentCardElement);
        this.mapSelector = new UIComponentItemScroll(mapSelectorContainer, this.getMapItemData());
        this.updateMapSelectorInteractability();
    }
}
UIModeSelectorBotMapController.BOT_MAP_MODE_SETTING = ModeSettingEnum.Classic4P;
UIModeSelectorBotMapController.DEFAULT_MAP_SETTING = MapSettingEnum.Classic4P;
